import {
  CobaltSideNavigation,
  CobaltSideNavigationItem,
  CobaltSideNavigationItemLabel,
  CobaltSideNavigationItemGroup,
  CobaltSideNavigationItemGroupButtonIcon,
  CobaltSideNavigationItemGroupButtonLabel,
  CobaltSideNavigationItemGroupItems,
} from '@cae/cobalt-react/components/side-navigation';
import { useModules } from '@contexts/modules-provider';
import { useOrganizations } from '@contexts/organizations-provider';
import { usePermissions } from '@contexts/permissions-provider';
import { useHasCompanyDocuments } from '@features/documents/hooks/use-has-company-documents/useHasCompanyDocuments';
import { APP_ROUTES } from '@routes/routes.constants';
import { checkPermissions } from '@shared/components/route-guards/permission-guard/PermissionGuard.utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import type { AppSideNavigationItem } from './AppSideNavigation.types';

import styles from './AppSideNavigation.module.css';
import {
  isActivePath as isActivePathHelper,
  resolvePath,
} from './AppSideNavigation.utils';
import { ModuleSwitcher } from './components/module-switcher/ModuleSwitcher';
import { OrganizationSwitcher } from './components/organization-switcher/OrganizationSwitcher';
import { STATIC_MODULE_NAV, COMMON_NAV } from './navigation';
import { getManageNav } from './navigation/manage-navigation';

const AppSideNavigation = React.memo((): React.ReactElement => {
  const location = useLocation();
  const { selectedModule } = useModules();
  const { selectedOrganization } = useOrganizations();
  const { hasPermission } = usePermissions();
  const flags = useFlags();
  const navigate = useNavigate();

  const currentPath = location.pathname;
  const isManageModule = selectedModule?.name === 'Manage';

  const { hasDocuments: hasCompanyDocuments } = useHasCompanyDocuments({
    organizationId: selectedOrganization?.id,
    enabled:
      isManageModule && hasPermission('CustomerTrainingDocument_View_Org'),
  });

  const visibleItems = React.useMemo(() => {
    let moduleNav: AppSideNavigationItem[];

    if (!selectedModule?.name) {
      moduleNav = COMMON_NAV;
    } else if (selectedModule.name === 'Manage') {
      moduleNav = getManageNav(
        selectedOrganization?.name ?? 'Documents',
        Boolean(flags.reservationsCalendar)
      );
    } else {
      moduleNav = STATIC_MODULE_NAV[selectedModule.name];
    }

    return moduleNav
      .map((item: AppSideNavigationItem) => {
        if (!item.children) {
          if (item.featureFlag && !flags[item.featureFlag]) return null;
          if (item.requiresPart135 && !selectedOrganization?.isPart135CertificateCode) return null;
          return checkPermissions(item.permission, hasPermission, item.operator)
            ? item
            : null;
        }
        if (item.featureFlag && !flags[item.featureFlag]) return null;
        if (item.requiresPart135 && !selectedOrganization?.isPart135CertificateCode) return null;
        if (!checkPermissions(item.permission, hasPermission, item.operator)) {
          return null;
        }

        const visibleChildren = item.children.filter(
          c =>
            (!c.featureFlag || Boolean(flags[c.featureFlag])) &&
            (!c.requiresPart135 || Boolean(selectedOrganization?.isPart135CertificateCode)) &&
            checkPermissions(c.permission, hasPermission, c.operator) &&
            (c.path !== APP_ROUTES.MANAGE_DOCUMENTS_COMPANY ||
              hasCompanyDocuments)
        );

        return visibleChildren.length > 0
          ? { ...item, children: visibleChildren }
          : null;
      })
      .filter(Boolean) as AppSideNavigationItem[];
  }, [
    selectedModule,
    selectedOrganization?.name,
    selectedOrganization?.isPart135CertificateCode,
    hasPermission,
    flags,
    hasCompanyDocuments,
  ]);

  const handleHeaderClick = React.useCallback(() => {
    navigate(APP_ROUTES.APP_INITIALIZER, { replace: true });
  }, [navigate]);

  const renderItem = React.useCallback(
    (item: AppSideNavigationItem, idx: number): React.ReactElement => {
      const resolvedPath = resolvePath(
        item.path,
        selectedOrganization?.customerId
      );
      const isActive = isActivePathHelper(currentPath, item.path);
      const Icon = isActive ? (item.activeIcon ?? item.icon) : item.icon;

      return (
        <CobaltSideNavigationItem activated={isActive} key={item.path ?? idx}>
          <NavLink
            to={resolvedPath}
            aria-label={item.label}
            className={styles.link}
          >
            {Icon ? <Icon size="medium" /> : null}
            <CobaltSideNavigationItemLabel>
              {item.label}
            </CobaltSideNavigationItemLabel>
          </NavLink>
        </CobaltSideNavigationItem>
      );
    },
    [currentPath, selectedOrganization?.customerId]
  );

  const renderGroup = React.useCallback(
    (group: AppSideNavigationItem, idx: number): React.ReactElement => {
      const isActive =
        group.children?.some(c => isActivePathHelper(currentPath, c.path)) ??
        false;

      const GroupIcon = group.icon;

      return (
        <CobaltSideNavigationItemGroup
          key={group.path ?? idx}
          activated={isActive}
          expanded={isActive}
        >
          <CobaltSideNavigationItemGroupButtonIcon>
            {GroupIcon ? <GroupIcon size="medium" /> : null}
          </CobaltSideNavigationItemGroupButtonIcon>
          <CobaltSideNavigationItemGroupButtonLabel>
            {group.label}
          </CobaltSideNavigationItemGroupButtonLabel>
          <CobaltSideNavigationItemGroupItems>
            {group.children?.map((child, i) => renderItem(child, i))}
          </CobaltSideNavigationItemGroupItems>
        </CobaltSideNavigationItemGroup>
      );
    },
    [currentPath, renderItem]
  );

  const orgSwitcherFooter =
    isManageModule && selectedOrganization ? (
      <OrganizationSwitcher />
    ) : undefined;

  return (
    <CobaltSideNavigation
      headerTitle="Connect"
      footer={orgSwitcherFooter}
      onHeaderClick={handleHeaderClick}
    >
      <ModuleSwitcher />
      {visibleItems.map((item: AppSideNavigationItem, idx: number) =>
        item.children ? renderGroup(item, idx) : renderItem(item, idx)
      )}
    </CobaltSideNavigation>
  );
});

AppSideNavigation.displayName = 'AppSideNavigation';

export { AppSideNavigation };
